import { IntlShape } from '@redbubble/next/intl';
import { useMemo } from 'react';
import { DefaultInventoryItem } from 'types/shop-bff/types';
import {
  availableProductStyleSwitchCategories,
  categoryProductStyleList,
} from './availableProductStyleSwitchCategories';
import { longStyleDescriptions } from './messages';

export interface ProductStyleData {
  productTypeId: string;
  previewImageUrl?: string | null;
  matureContent?: boolean;
  description: string;
  longDescription: string;
  productPageUrl?: string;
  price: NonNullable<DefaultInventoryItem>['price'];
  gaCode: string;
}
export interface UseProductStyleDataResult {
  currentProductStyle?: ProductStyleData;
  popularProductStyles: ProductStyleData[];
  moreProductStyles: ProductStyleData[];
  hiddenProductStyles: ProductStyleData[];
  availableProducts: DefaultInventoryItem[];
  aaoProductCount: number;
  matureContent?: boolean;
}

const defaultUseProductStyleDataResult: UseProductStyleDataResult = {
  currentProductStyle: undefined,
  popularProductStyles: [],
  moreProductStyles: [],
  hiddenProductStyles: [],
  aaoProductCount: 0,
  availableProducts: [],
  matureContent: false,
};

const DEFAULT_POPULAR_COUNT = 3;
const DEFAULT_MORE_COUNT = 4;
const PRODUCT_CLOSE_PREVIEW = 'product_close';

/**
 * The long descriptions are hardcoded in the frontend
 */
export function getProductLongDescription(
  intl: IntlShape,
  productTypeId: string
) {
  const longDesc = longStyleDescriptions[productTypeId];

  if (!longDesc) return '';
  return intl.formatMessage(longDesc);
}

export function getProductImagePreview(product: DefaultInventoryItem) {
  const previews = product?.previewSet?.previews || [];

  if (previews.length === 0) return null;

  const productClose = previews.find(
    (image) => image.previewTypeId === PRODUCT_CLOSE_PREVIEW
  );

  if (productClose?.url) {
    return productClose.url;
  }

  return previews[0]?.url || null;
}

/**
 * @returns the product's category if the product's category has support for style switching
 */
export const getProductSwitchCategoryName = (productTypeId: string) => {
  return availableProductStyleSwitchCategories.find((category) => {
    return categoryProductStyleList[category]?.find(
      (categoryProductTypeId: string) => categoryProductTypeId === productTypeId
    );
  });
};

/**
 * @returns ordered list of productTypeIds if the product's category has support for style switching
 */
function getProductStylesList(productTypeId: string) {
  const categoryName = getProductSwitchCategoryName(productTypeId); // filters the categoryProductList to get the right category (e.g. t-shirt)

  if (!categoryName) return undefined;

  const productList = categoryProductStyleList[categoryName]; // a list of all the styles for selected category

  if (!productList) return undefined;

  return productList;
}

/**
 * @param currentProductTypeId
 * @param availableProducts array of products from the aao query
 * @param popularProductCount number of products to be placed in the popularProductCount
 * @param moreProductCount number of remaining products to be placed under the popular Product section. Any remaining products will be hidden behind a "see more" button
 */
export function useProductStyleData({
  intl,
  currentProductTypeId, // used to get the category
  availableProducts,
  matureContent,
  popularProductCount = DEFAULT_POPULAR_COUNT,
  moreProductCount = DEFAULT_MORE_COUNT,
}: {
  intl: IntlShape;
  currentProductTypeId: string;
  availableProducts: DefaultInventoryItem[];
  matureContent?: boolean;
  popularProductCount?: number;
  moreProductCount?: number;
}): UseProductStyleDataResult {
  return useMemo(() => {
    const productList = getProductStylesList(currentProductTypeId);

    if (!productList) return defaultUseProductStyleDataResult;

    // filter the available products array by the hardcoded productType
    const filteredAvailableProducts: DefaultInventoryItem[] =
      productList.flatMap((productTypeId: string) => {
        return availableProducts.filter((availableProduct) => {
          return availableProduct?.productTypeId === productTypeId;
        });
      });

    // transforms the data into an object with required data only
    const availableProductStyles: ProductStyleData[] =
      filteredAvailableProducts.map((product) => {
        const productTypeId = product?.productTypeId || '';
        return {
          productTypeId,
          previewImageUrl: getProductImagePreview(product),
          matureContent,
          description: product?.description || '',
          longDescription: getProductLongDescription(intl, productTypeId),
          productPageUrl: product?.productPageUrls?.fallbackUrl || undefined,
          price: product?.price || undefined,
          gaCode: product?.gaCode || '',
        };
      });

    const popularProductStyles = availableProductStyles.slice(
      0,
      popularProductCount
    );

    const moreProductStyles = availableProductStyles.slice(
      popularProductCount,
      popularProductCount + moreProductCount
    );

    const hiddenProductStyles = availableProductStyles.slice(
      popularProductCount + moreProductCount
    );

    const currentProductStyle = availableProductStyles.find(
      (item) => item.productTypeId === currentProductTypeId
    );

    return {
      ...defaultUseProductStyleDataResult,
      currentProductStyle,
      popularProductStyles,
      moreProductStyles,
      hiddenProductStyles,
      // minus 1 because the current product is included in the count
      aaoProductCount: availableProducts ? availableProducts.length - 1 : 0,
      availableProducts,
      matureContent,
    };
  }, [
    intl,
    currentProductTypeId,
    availableProducts,
    matureContent,
    popularProductCount,
    moreProductCount,
  ]);
}
